import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CardActions,
  Button, Chip, CircularProgress, Alert,
} from '@mui/material';
import UploadFileIcon   from '@mui/icons-material/UploadFile';
import DownloadIcon     from '@mui/icons-material/Download';
import OpenInNewIcon    from '@mui/icons-material/OpenInNew';
import SwapHorizIcon    from '@mui/icons-material/SwapHoriz';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import { authGet } from '../../services/apiService';

// ── Month helpers ──────────────────────────────────────────────────────────────

const MONTHS = [
  { key: '2026-07', label: 'July 2026' },
  { key: '2026-08', label: 'August 2026' },
  { key: '2026-09', label: 'September 2026' },
  { key: '2026-10', label: 'October 2026' },
  { key: '2026-11', label: 'November 2026' },
  { key: '2026-12', label: 'December 2026' },
];

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Main component ─────────────────────────────────────────────────────────────

const StabilityImprovementsTab = () => {
  const [records,   setRecords]   = useState({});  // { monthKey → record }
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [uploading, setUploading] = useState({});  // { monthKey → bool }
  const [uploadErr, setUploadErr] = useState({});  // { monthKey → string }
  const fileRefs = useRef({});

  const loadRecords = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const months = MONTHS.map(m => m.key).join(',');
      const res = await authGet(`/api/stability-reports?months=${months}`);
      if (res?.success) {
        const map = {};
        (res.data || []).forEach(r => { map[r.monthKey] = r; });
        setRecords(map);
      } else {
        setError('Failed to load reports');
      }
    } catch {
      setError('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const handleFileSelect = (monthKey) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setUploading(u => ({ ...u, [monthKey]: true }));
    setUploadErr(u => ({ ...u, [monthKey]: null }));
    try {
      const token = localStorage.getItem('token');
      const form  = new FormData();
      form.append('file', file);
      const res  = await fetch(`/api/stability-reports/${monthKey}/upload`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
        body:    form,
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Upload failed');
      setRecords(r => ({ ...r, [monthKey]: json.data }));
    } catch (err) {
      setUploadErr(u => ({ ...u, [monthKey]: err.message }));
    } finally {
      setUploading(u => ({ ...u, [monthKey]: false }));
    }
  };

  const handleDownload = (monthKey, fileName) => async () => {
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`/api/stability-reports/${monthKey}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const handleView = (monthKey) => async () => {
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`/api/stability-reports/${monthKey}/view`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('View failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {}
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const uploaded = MONTHS.filter(m => records[m.key]);
  const pending  = MONTHS.filter(m => !records[m.key]);

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* About */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, borderLeft: '4px solid', borderColor: 'primary.main' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: '#1D4434' }}>
          About
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Stability &amp; performance improvements report for the given month, demonstrating the improvements delivered in the product.
        </Typography>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Chip
          label={`${uploaded.length} / ${MONTHS.length} uploaded`}
          color={uploaded.length === MONTHS.length ? 'success' : 'default'}
          size="small"
        />
        <Typography variant="body2" color="text.secondary">
          Jul – Dec 2026 · any file format · 50 MB limit
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {MONTHS.map(month => (
          <Grid item xs={12} sm={6} md={4} key={month.key}>
            <MonthCard
              month={month}
              record={records[month.key] || null}
              uploading={!!uploading[month.key]}
              uploadErr={uploadErr[month.key]}
              fileRef={el => { fileRefs.current[month.key] = el; }}
              onUploadClick={() => fileRefs.current[month.key]?.click()}
              onFileSelect={handleFileSelect(month.key)}
              onDownload={handleDownload(month.key, records[month.key]?.fileName)}
              onView={handleView(month.key)}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ── Month card ─────────────────────────────────────────────────────────────────

const MonthCard = ({ month, record, uploading, uploadErr, fileRef, onUploadClick, onFileSelect, onDownload, onView }) => {
  const hasUpload = !!record;

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor: hasUpload ? 'success.light' : 'divider',
        borderWidth: hasUpload ? 2 : 1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2, py: 1.5,
          bgcolor: hasUpload ? 'success.50' : 'grey.50',
          borderBottom: '1px solid',
          borderColor: hasUpload ? 'success.light' : 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <CalendarTodayIcon sx={{ fontSize: 16, color: hasUpload ? 'success.main' : 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
          {month.label}
        </Typography>
        {hasUpload && <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />}
      </Box>

      <CardContent sx={{ flex: 1, py: 1.5, px: 2 }}>
        {hasUpload ? (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, wordBreak: 'break-all' }}>
              {record.fileName}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              By {record.uploadedByName}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {fmtDate(record.uploadedAt)} · {fmtSize(record.fileSize)}
            </Typography>
            {record.overrideCount > 0 && (
              <Chip
                label={`Updated ${record.overrideCount}×`}
                size="small"
                sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}
              />
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No report uploaded yet
          </Typography>
        )}
        {uploadErr && (
          <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
            {uploadErr}
          </Typography>
        )}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 1.5, pt: 0, gap: 1 }}>
        <input
          type="file"
          style={{ display: 'none' }}
          ref={fileRef}
          onChange={onFileSelect}
        />
        <Button
          size="small"
          variant={hasUpload ? 'outlined' : 'contained'}
          startIcon={uploading
            ? <CircularProgress size={12} color="inherit" />
            : hasUpload ? <SwapHorizIcon /> : <UploadFileIcon />
          }
          onClick={onUploadClick}
          disabled={uploading}
          color={hasUpload ? 'warning' : 'primary'}
          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
        >
          {uploading ? 'Uploading…' : hasUpload ? 'Override' : 'Upload'}
        </Button>
        {hasUpload && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={onView}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            View
          </Button>
        )}
        {hasUpload && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={onDownload}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Download
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default StabilityImprovementsTab;
